import { httpClient } from '@/shared/services/HttpClient';
import { CancelToken } from 'axios';
import qs from 'qs';
import config from '@/shared/constants/config';

class AdImageSettings {
  height?: number | null = null;
  width?: number | null = null;
  rmode?: string | null = null;
  hrmode?: string | null = null;
  wrmode?: string | null = null;
  bgcolor?: string | null = null;
  watermark = true;
}

class AdService {
  url = 'operation/estate';

  getDetail(adFrontId: string, params?: any): any {
    return httpClient.get(this.url + '/detail/' + adFrontId, { params });
  }

  getDescription(adFrontId: string, params?: any): any {
    return httpClient.get(this.url + '/description/' + adFrontId, { params });
  }

  getById(adFrontId: string): any {
    return httpClient.get(this.url + '/' + adFrontId);
  }

  get(params: any, cancelToken?: CancelToken): any {
    const qsString = qs.stringify(params, { skipNulls: true, encodeValuesOnly: true });
    return httpClient.get(this.url + '?' + qsString, { cancelToken });
  }

  getMarkers(params: any, cancelToken?: CancelToken): any {
    const qsString = qs.stringify(params, { skipNulls: true, encodeValuesOnly: true });
    return httpClient.get(this.url + '/markers?' + qsString, { cancelToken });
  }

  getMyAgencyList(params: any): any {
    const qsString = qs.stringify(params, { skipNulls: true, encodeValuesOnly: true });
    return httpClient.get(this.url + '/agency/me?' + qsString);
  }

  getUserList(params: any): any {
    const qsString = qs.stringify(params, { skipNulls: true, encodeValuesOnly: true });
    return httpClient.get(this.url + '/me?' + qsString);
  }

  getDeveloperProjects(params: any): any {
    const qsString = qs.stringify(params, { skipNulls: true, encodeValuesOnly: true });
    return httpClient.get(this.url + '/developer-project/me?' + qsString);
  }

  getFavoriteList(params: any): any {
    const qsString = qs.stringify(params, { skipNulls: true, encodeValuesOnly: true });
    return httpClient.get(this.url + '/favorite?' + qsString);
  }

  getSimilar(params: any): any {
    const qsString = qs.stringify(params, { skipNulls: true, encodeValuesOnly: true });
    return httpClient.get(this.url + '/similar?' + qsString);
  }

  getLocations(params: any): any {
    const qsString = qs.stringify(params, { skipNulls: true, encodeValuesOnly: true });
    return httpClient.get(this.url + '/locations?' + qsString);
  }

  getFilter(params?: any): any {
    const qsString = qs.stringify(params, { skipNulls: true, encodeValuesOnly: true });
    return httpClient.get(this.url + '/filter?' + qsString);
  }

  getNew(): any {
    return httpClient.get(this.url + '/new');
  }

  publish(adId: number): any {
    return httpClient.put(this.url + '/publish', { adId, isPublished: true });
  }

  unpublish(adId: number): any {
    return httpClient.put(this.url + '/publish', { adId, isPublished: false });
  }

  reserve(adId: number): any {
    return httpClient.put(this.url + '/reserve', { adId, isReserved: true });
  }

  unreserve(adId: number): any {
    return httpClient.put(this.url + '/reserve', { adId, isReserved: false });
  }

  archive(adId: number): any {
    return httpClient.put(this.url + '/archive', { adId, isArchived: true });
  }

  unarchive(adId: number): any {
    return httpClient.put(this.url + '/archive', { adId, isArchived: false });
  }

  insert(ad: any): any {
    return httpClient.post(this.url, ad);
  }

  update(ad: any): any {
    return httpClient.put(this.url, ad);
  }

  updateInternalNote(ad: any): any {
    return httpClient.put(this.url + '/internal-note', ad);
  }

  interested(form: any): any {
    return httpClient.post(this.url + '/interested', form);
  }

  addComplaint(complaint: any): any {
    return httpClient.post(this.url + '/complaint', complaint);
  }

  uploadImage(formData: any, onUploadProgress: any): any {
    return httpClient.post(this.url + '/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: onUploadProgress,
    });
  }

  getImageUrl(adGuid: string, file: string, settings: AdImageSettings): string | null {
    if (adGuid && file) {
      const querystring = qs.stringify({
        height: settings.height,
        width: settings.width,
        rmode: settings.rmode,
        hrmode: settings.hrmode,
        wrmode: settings.wrmode,
        bgcolor: settings.bgcolor,
        watermark: settings.watermark ? '1' : '0',
      });
      const url = config.BASE_URL + '/files/pictures/' + adGuid + '/' + file;
      if (querystring) {
        return url + '?' + querystring;
      }
      return url;
    } else {
      return null;
    }
  }

  getImageOrPlaceHolder(adGuid: string, estateType: number, estateSubType: number, file: string, settings: AdImageSettings): string | null {
    const url = this.getImageUrl(adGuid, file, settings);
    if (url) {
      return url;
    }
    return this.getPlaceHolder(estateType, estateSubType);
  }

  getPlaceHolder(estateType: number, estateSubType: number) {
    if (estateType === 100) {
      return config.BASE_URL + '/img/no-photo-flat.jpg';
    } else if (estateType === 200) {
      return config.BASE_URL + '/img/no-photo-house.jpg';
    } else if (estateType === 300) {
      return config.BASE_URL + '/img/no-photo-land.jpg';
    } else if (estateType === 400) {
      if (estateSubType === 401) return config.BASE_URL + '/img/no-photo-parking.jpg';
      return config.BASE_URL + '/img/no-photo-office.jpg';
    } else {
      return config.BASE_URL + '/img/no-photo.jpg';
    }
  }

  view(adId: number): any {
    return httpClient.post(this.url + '/' + adId + '/view');
  }

  addFavorite(adId: number): any {
    return httpClient.post(this.url + '/' + adId + '/favorite');
  }

  removeFavorite(adId: number): any {
    return httpClient.delete(this.url + '/' + adId + '/favorite');
  }

  getCompare(params: any): any {
    const qsString = qs.stringify(params, { skipNulls: true, encodeValuesOnly: true });
    return httpClient.get(this.url + '/compare?' + qsString);
  }

  addCompare(adId: number): any {
    return httpClient.post(this.url + '/' + adId + '/compare');
  }

  removeCompare(adId: number): any {
    return httpClient.delete(this.url + '/' + adId + '/compare');
  }

  like(adId: number): any {
    return httpClient.post(this.url + '/' + adId + '/like');
  }

  unlike(adId: number): any {
    return httpClient.delete(this.url + '/' + adId + '/unlike');
  }

  watch(adId: number): any {
    return httpClient.post(this.url + '/' + adId + '/watch');
  }

  unwatch(adId: number): any {
    return httpClient.delete(this.url + '/' + adId + '/unwatch');
  }

  getStatistics(params: any): any {
    const qsString = qs.stringify(params, { skipNulls: true, encodeValuesOnly: true });
    return httpClient.get(this.url + '/statistics?' + qsString);
  }

  share(adId: number): any {
    return httpClient.post(this.url + '/' + adId + '/share');
  }

  getViewsStatistics(adId: number, params: any): any {
    const qsString = qs.stringify(params, { skipNulls: true, encodeValuesOnly: true });
    return httpClient.get(this.url + '/' + adId + '/views?' + qsString);
  }

  getLocation(params: any): any {
    const qsString = qs.stringify(params, { skipNulls: true, encodeValuesOnly: true });
    return httpClient.get(this.url + '/location?' + qsString);
  }

  getPlace(place_id: string) {
    return new Promise(function (resolve) {
      const request = {
        placeId: place_id,
        fields: ['name', 'formatted_address', 'address_components', 'place_id', 'geometry'],
      };
      const placeService = new google.maps.places.PlacesService(document.createElement('div'));

      placeService.getDetails(request, (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place && place.geometry && place.geometry.location) {
          resolve(place);
        } else {
          resolve(null);
        }
      });
    });
  }

  async getParamsToUrl(searchParams, place, adFilter) {
    const params = {
      q: null,
      q2: null,
      q3: null,
    };

    const titleParams = {
      location: null,
      adType: null,
      estateType: null,
    };

    if (place && place.placeResult && place.placeResult.place_id) {
      try {
        const location = await adService.getLocation({ placeID: place.placeResult.place_id });
        if (location.status === 200 && location.data) {
          params.q = location.data.code;
          titleParams.location = location.data;
        }
      } catch {
        params.q = null;
      }

      if (!titleParams.location && place && place.address && place.address.city) {
        titleParams.location = {
          title: place.address.city,
        };
      }
    }

    if (searchParams.type) {
      const adType = adFilter.adTypes.find((x) => x.key === searchParams.type);
      if (adType) {
        params.q2 = adType.code;
        titleParams.adType = adType;
        searchParams.type = null;
      }
    }

    if (searchParams.estateFrontSubTypes && searchParams.estateFrontSubTypes.length === 1) {
      for (const estateType of adFilter.estateTypes) {
        const estateSubType = estateType.estateSubTypes.find((x) => x.key === searchParams.estateFrontSubTypes[0]);
        if (estateSubType) {
          params.q3 = estateSubType.code;
          titleParams.estateType = estateSubType;
          searchParams.estateFrontSubTypes = [];
        }
      }
    }

    const qsString = qs.stringify(searchParams, { skipNulls: true, encodeValuesOnly: true });
    const routerParams = { name: 'AdSearch', params: params };

    return {
      qsString,
      routerParams,
      titleParams,
    };
  }

  getSortByTypes($t: any) {
    return [
      { value: 'createdUtc desc', title: $t('AdService.sort-types.created-desc'), icon: 'icon-sort-desc' },
      { value: 'createdUtc', title: $t('AdService.sort-types.created'), icon: 'icon-sort-asc' },
      { value: 'price', title: $t('AdService.sort-types.price'), icon: 'icon-sort-desc' },
      { value: 'price desc', title: $t('AdService.sort-types.price-desc'), icon: 'icon-sort-asc' },
      { value: 'likeCount desc', title: $t('AdService.sort-types.like-count-desc'), icon: 'icon-like-desc' },
    ];
  }

  insertDeveloperProject(ad: any): any {
    return httpClient.post(this.url + '/developer-project', ad);
  }

  updateDeveloperProject(ad: any): any {
    return httpClient.put(this.url + '/developer-project', ad);
  }

  uploadDeveloperProjectLogo(formData: any, onUploadProgress: any): any {
    return httpClient.post(this.url + '/developer-project/logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: onUploadProgress,
    });
  }

  insertDeveloperProjectEstate(ad: any): any {
    return httpClient.post(this.url + '/developer-project/estate', ad);
  }

  getDeveloperProjectItems(params: any): any {
    const qsString = qs.stringify(params, { skipNulls: true, encodeValuesOnly: true });
    return httpClient.get(this.url + '/developer-project/estate?' + qsString);
  }

  getMyDeveloperProjectItems(params: any): any {
    const qsString = qs.stringify(params, { skipNulls: true, encodeValuesOnly: true });
    return httpClient.get(this.url + '/developer-project/estate/me?' + qsString);
  }

  uploadImport(formData: any, onUploadProgress: any): any {
    return httpClient.post(this.url + '/developer-project/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: onUploadProgress,
    });
  }

  startImport(data: any): any {
    return httpClient.post(this.url + '/developer-project/import-start', data);
  }
}

const adService = new AdService();

export { adService, AdImageSettings };
